import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const AdminProducts = ({ showAddForm = false }) => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(showAddForm);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    imageUrl: '',
    category: '',
    stock: '',
    featured: false,
    freeDelivery: false,
    shippingPrice: '0.00'
  });
  // We're using this state for image preview functionality
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    stock: '',
    featured: ''
  });
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check for query params and props
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const stockParam = queryParams.get('stock');
    
    if (stockParam === 'low') {
      setFilters(prev => ({ ...prev, stock: 'low' }));
    }
    
    // Show form if navigated from dashboard with showAddForm prop
    if (showAddForm) {
      setShowForm(true);
    }
  }, [location.search, showAddForm]);
  
  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await api.get('/api/products');
        
        setProducts(res.data);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(res.data.map(product => product.category))];
        setCategories(uniqueCategories);
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);
  
  // Apply filters
  useEffect(() => {
    let result = [...products];
    
    // Filter by search term
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(
        product => 
          product.name.toLowerCase().includes(searchTerm) || 
          product.description.toLowerCase().includes(searchTerm)
      );
    }
    
    // Filter by category
    if (filters.category) {
      result = result.filter(product => product.category === filters.category);
    }
    
    // Filter by stock
    if (filters.stock === 'low') {
      result = result.filter(product => product.stock < 5);
    } else if (filters.stock === 'out') {
      result = result.filter(product => product.stock === 0);
    }
    
    // Filter by featured
    if (filters.featured === 'yes') {
      result = result.filter(product => product.featured);
    } else if (filters.featured === 'no') {
      result = result.filter(product => !product.featured);
    }
    
    setFilteredProducts(result);
  }, [filters, products]);
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      stock: '',
      featured: ''
    });
    
    // Clear URL params
    navigate('/admin/products');
  };
  
  // Function to compress image before upload
  const compressImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          // Create canvas
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw image on canvas
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Get compressed data URL
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(dataUrl);
        };
        img.onerror = error => reject(error);
      };
      reader.onerror = error => reject(error);
    });
  };
  
  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      // Handle image file upload
      if (files && files[0]) {
        const file = files[0];
        
        // Only accept image files
        if (!file.type.match('image.*')) {
          setFormErrors(prev => ({ ...prev, imageUrl: 'Please select an image file' }));
          return;
        }
        
        try {
          // Show original image as preview immediately
          const reader = new FileReader();
          reader.onload = (e) => {
            setImagePreview(e.target.result);
          };
          reader.readAsDataURL(file);
          
          // Compress the image
          const compressedImage = await compressImage(file);
          
          // Store the compressed image URL in formData
          setFormData(prev => ({
            ...prev,
            imageUrl: compressedImage
          }));
          
          // Store the file for later upload if needed
          setImageFile(file);
        } catch (error) {
          console.error('Error processing image:', error);
          setFormErrors(prev => ({ ...prev, imageUrl: 'Error processing image. Please try again.' }));
        }
      }
    } else {
      // Handle other form fields
      const newValue = type === 'checkbox' ? checked : value;
      setFormData(prev => ({
        ...prev,
        [name]: newValue
      }));
      
      // If this is a shipping-related field and we're in edit mode, update shipping settings immediately
      if (editMode && editId && (name === 'freeDelivery' || name === 'shippingPrice')) {
        updateShippingSettings({
          freeShipping: name === 'freeDelivery' ? newValue : formData.freeDelivery,
          shippingFee: name === 'shippingPrice' ? parseFloat(newValue) : parseFloat(formData.shippingPrice)
        });
      }
    }
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Function to directly update shipping settings
  const updateShippingSettings = async (shippingData) => {
    try {
      console.log('Updating shipping settings for product:', editId, shippingData);
      
      // Use the regular product update endpoint, but only send shipping fields
      const res = await api.put(`/api/products/${editId}`, {
        ...shippingData,
        // Include both field naming conventions to ensure it works
        freeDelivery: shippingData.freeShipping
      });
      
      // Update the product in the list
      setProducts(prev => 
        prev.map(product => 
          product._id === editId ? { ...product, ...res.data } : product
        )
      );
      
      console.log('Shipping settings updated successfully:', res.data);
    } catch (err) {
      console.error('Failed to update shipping settings:', err);
      alert('Failed to update shipping settings. Please try again.');
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    if (!formData.price) {
      errors.price = 'Price is required';
    } else if (isNaN(formData.price) || parseFloat(formData.price) <= 0) {
      errors.price = 'Price must be a positive number';
    }
    
    if (!formData.imageUrl.trim()) {
      errors.imageUrl = 'Image URL is required';
    }
    
    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    }
    
    if (!formData.stock) {
      errors.stock = 'Stock is required';
    } else if (isNaN(formData.stock) || parseInt(formData.stock) < 0) {
      errors.stock = 'Stock must be a non-negative number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create a clean object without the formData spread to avoid any unexpected fields
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        imageUrl: formData.imageUrl,
        category: formData.category,
        stock: parseInt(formData.stock),
        featured: formData.featured,
        // IMPORTANT: Explicitly set both field names to ensure they're saved
        freeShipping: Boolean(formData.freeDelivery),
        shippingFee: parseFloat(formData.shippingPrice)
      };
      
      console.log('Saving product with data:', JSON.stringify(productData, null, 2));
      
      let res;
      
      if (editMode) {
        // Update existing product
        res = await api.put(`/api/products/${editId}`, productData);
        
        // Update products list
        setProducts(prev => 
          prev.map(product => 
            product._id === editId ? res.data : product
          )
        );
        
        // Reset form and edit mode
        setEditMode(false);
        setEditId(null);
      } else {
        // Create new product
        res = await api.post('/api/products', productData);
        
        // Add new product to list
        setProducts(prev => [res.data, ...prev]);
      }
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        price: '',
        imageUrl: '',
        category: '',
        stock: '',
        featured: false,
        freeDelivery: false,
        shippingPrice: '0.00'
      });
      
      setShowForm(false);
    } catch (err) {
      console.error('Error saving product:', err);
      setFormErrors({ submit: err.response?.data?.msg || 'Failed to save product' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleEdit = (product) => {
    console.log('Editing product with data:', product);
    
    // Determine if product has free shipping/delivery
    const hasFreeDelivery = product.freeDelivery === true || product.freeShipping === true;
    
    // Get shipping price from either field
    const shippingPrice = product.shippingPrice || product.shippingFee || 0.00;
    
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      imageUrl: product.imageUrl,
      category: product.category,
      stock: product.stock.toString(),
      featured: product.featured,
      // Add shipping-related fields with explicit boolean conversion
      freeDelivery: hasFreeDelivery,
      shippingPrice: shippingPrice.toString()
    });
    
    console.log('Form data set to:', {
      name: product.name,
      freeDelivery: hasFreeDelivery,
      shippingPrice: shippingPrice.toString()
    });
    
    setEditMode(true);
    setEditId(product._id);
    setShowForm(true);
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    
    try {
      await api.delete(`/api/products/${id}`);
      
      // Remove product from list
      setProducts(prev => prev.filter(product => product._id !== id));
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Failed to delete product');
    }
  };
  
  const toggleForm = () => {
    if (showForm && editMode) {
      // Reset form if canceling edit
      setFormData({
        name: '',
        description: '',
        price: '',
        imageUrl: '',
        category: '',
        stock: '',
        featured: false,
        freeDelivery: false,
        shippingPrice: '0.00'
      });
      setEditMode(false);
      setEditId(null);
    }
    
    setShowForm(!showForm);
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8">
        <div className="neumorph p-6 rounded-lg inline-block">
          <h3 className="text-xl text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="neumorph-btn mt-4 px-4 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Manage Products</h1>
        <button 
          onClick={toggleForm}
          className="neumorph-btn px-4 py-2"
        >
          {showForm ? 'Cancel' : 'Add New Product'}
        </button>
      </div>
      
      {/* Product Form */}
      {console.log('Rendering component, showForm is:', showForm)}
      {showForm && (
        <div className="neumorph p-6 rounded-lg mb-8">
          <h2 className="text-xl font-semibold mb-4">
            {editMode ? 'Edit Product' : 'Add New Product'}
          </h2>
          
          {formErrors.submit && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {formErrors.submit}
            </div>
          )}
          
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-gray-700 mb-2">Product Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.name ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.name && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
                )}
              </div>
              
              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-gray-700 mb-2">Category</label>
                <input
                  type="text"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  list="categories"
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.category ? 'border border-red-500' : ''
                  }`}
                />
                <datalist id="categories">
                  {categories.map(category => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
                {formErrors.category && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.category}</p>
                )}
              </div>
              
              {/* Price */}
              <div>
                <label htmlFor="price" className="block text-gray-700 mb-2">Price</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.price ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.price && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.price}</p>
                )}
              </div>
              
              {/* Stock */}
              <div>
                <label htmlFor="stock" className="block text-gray-700 mb-2">Stock</label>
                <input
                  type="number"
                  id="stock"
                  name="stock"
                  value={formData.stock}
                  onChange={handleChange}
                  min="0"
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.stock ? 'border border-red-500' : ''
                  }`}
                />
                {formErrors.stock && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.stock}</p>
                )}
              </div>
              
              {/* Product Image */}
              <div className="md:col-span-2">
                <label htmlFor="imageUpload" className="block text-gray-700 mb-2">Product Image</label>
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Image Upload */}
                  <div className="flex-1">
                    <div className="neumorph-inset p-4 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="file"
                        id="imageUpload"
                        name="imageUpload"
                        accept="image/*"
                        onChange={handleChange}
                        className="hidden"
                      />
                      <label htmlFor="imageUpload" className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
                        <svg className="h-10 w-10 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-600">Click to upload image</span>
                        <span className="text-xs text-gray-500 mt-1">or drag and drop</span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">Supported formats: JPG, PNG, GIF</p>
                  </div>
                  
                  {/* Image Preview */}
                  <div className="flex-1">
                    {(imagePreview || formData.imageUrl) ? (
                      <div className="neumorph p-2 rounded-lg">
                        <img 
                          src={imagePreview || formData.imageUrl} 
                          alt="Product preview" 
                          className="w-full h-40 object-contain rounded-lg"
                        />
                      </div>
                    ) : (
                      <div className="neumorph p-2 rounded-lg flex items-center justify-center h-40 bg-gray-50">
                        <span className="text-sm text-gray-500">Image preview will appear here</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Alternative URL input */}
                <div className="mt-4">
                  <label htmlFor="imageUrl" className="block text-gray-700 mb-2">Or enter image URL</label>
                  <input
                    type="text"
                    id="imageUrl"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleChange}
                    className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                      formErrors.imageUrl ? 'border border-red-500' : ''
                    }`}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                
                {formErrors.imageUrl && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.imageUrl}</p>
                )}
              </div>
              
              {/* Description */}
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-gray-700 mb-2">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.description ? 'border border-red-500' : ''
                  }`}
                ></textarea>
                {formErrors.description && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.description}</p>
                )}
              </div>
              
              {/* Featured & Delivery Options */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span>Featured Product</span>
                  </label>
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="freeDelivery"
                      checked={formData.freeDelivery}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    <span>Free Delivery</span>
                  </label>
                </div>
              </div>
              
              {/* Shipping Price */}
              <div className="md:col-span-2">
                <label htmlFor="shippingPrice" className="block text-gray-700 mb-2">Shipping Price ($)</label>
                <input
                  type="number"
                  id="shippingPrice"
                  name="shippingPrice"
                  value={formData.shippingPrice}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  disabled={formData.freeDelivery}
                  className={`neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none ${
                    formErrors.shippingPrice ? 'border border-red-500' : ''
                  } ${
                    formData.freeDelivery ? 'bg-gray-100 text-gray-500' : ''
                  }`}
                />
                {formErrors.shippingPrice && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.shippingPrice}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">Set the shipping cost for this product. This will be overridden if free delivery is enabled.</p>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="submit"
                className="neumorph-btn px-6 py-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : (editMode ? 'Update Product' : 'Add Product')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Debug information */}
      <div className="mt-4 p-2 bg-gray-100 rounded">
        <p>Debug: showForm = {String(showForm)}</p>
      </div>
      
      {/* Filters */}
      <div className="neumorph p-6 rounded-lg mb-8">
        <h2 className="text-xl font-semibold mb-4">Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label htmlFor="search" className="block text-gray-700 mb-2">Search</label>
            <input
              type="text"
              id="search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              className="neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none"
              placeholder="Search products..."
            />
          </div>
          
          {/* Category Filter */}
          <div>
            <label htmlFor="category-filter" className="block text-gray-700 mb-2">Category</label>
            <select
              id="category-filter"
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              className="neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          
          {/* Stock Filter */}
          <div>
            <label htmlFor="stock-filter" className="block text-gray-700 mb-2">Stock</label>
            <select
              id="stock-filter"
              name="stock"
              value={filters.stock}
              onChange={handleFilterChange}
              className="neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none"
            >
              <option value="">All Stock Levels</option>
              <option value="low">Low Stock (&lt; 5)</option>
              <option value="out">Out of Stock</option>
            </select>
          </div>
          
          {/* Featured Filter */}
          <div>
            <label htmlFor="featured-filter" className="block text-gray-700 mb-2">Featured</label>
            <select
              id="featured-filter"
              name="featured"
              value={filters.featured}
              onChange={handleFilterChange}
              className="neumorph-inset w-full px-4 py-2 rounded-lg focus:outline-none"
            >
              <option value="">All Products</option>
              <option value="yes">Featured Only</option>
              <option value="no">Non-Featured Only</option>
            </select>
          </div>
        </div>
        
        <div className="mt-4 flex justify-end">
          <button
            onClick={clearFilters}
            className="neumorph-btn px-4 py-2"
          >
            Clear Filters
          </button>
        </div>
      </div>
      
      {/* Products Table */}
      <div className="neumorph p-6 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Products ({filteredProducts.length})</h2>
        
        {filteredProducts.length === 0 ? (
          <p className="text-gray-600 text-center py-4">No products found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold">Image</th>
                  <th className="pb-3 font-semibold">Name</th>
                  <th className="pb-3 font-semibold">Category</th>
                  <th className="pb-3 font-semibold">Price</th>
                  <th className="pb-3 font-semibold">Stock</th>
                  <th className="pb-3 font-semibold">Featured</th>
                  <th className="pb-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product._id} className="border-b last:border-b-0">
                    <td className="py-4">
                      <div className="neumorph p-1 rounded-lg w-12 h-12">
                        <img 
                          src={product.imageUrl} 
                          alt={product.name} 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    </td>
                    <td className="py-4">
                      <Link to={`/products/${product._id}`} className="hover:text-blue-600">
                        {product.name}
                      </Link>
                    </td>
                    <td className="py-4">{product.category}</td>
                    <td className="py-4">${product.price.toFixed(2)}</td>
                    <td className="py-4">
                      <span className={`${
                        product.stock === 0 
                          ? 'text-red-600' 
                          : product.stock < 5 
                            ? 'text-yellow-600' 
                            : 'text-green-600'
                      }`}>
                        {product.stock}
                      </span>
                    </td>
                    <td className="py-4">
                      {product.featured ? (
                        <span className="text-green-600">Yes</span>
                      ) : (
                        <span className="text-gray-400">No</span>
                      )}
                    </td>
                    <td className="py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(product)}
                          className="neumorph-btn px-3 py-1 text-sm"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(product._id)}
                          className="neumorph-btn px-3 py-1 text-sm bg-red-50 text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProducts;
